import { useIntl } from 'react-intl';
import {
  DEFAULT_COLUMNS,
  DEFAULT_PARTIAL_INVOICE_COLUMNS,
} from '../../../constants/pdf-preview.ts';
import { usePdfPreviewContext } from '../../pdf-preview-context.tsx';

interface UsePdfPreviewColumnsReturnValue {
  columns: { id: string; value: string }[];
}

export function usePdfPreviewColumns(): UsePdfPreviewColumnsReturnValue {
  const { formatMessage } = useIntl();
  const { displayLanguage, settings, isPartialInvoice } = usePdfPreviewContext();

  const getColumns = () => {
    const defaultColumns = isPartialInvoice ? DEFAULT_PARTIAL_INVOICE_COLUMNS : DEFAULT_COLUMNS;
    const columns = settings?.tableColumns ?? defaultColumns;

    if (!isPartialInvoice) return columns;

    const missingColumns = ['progress', 'cumulative_progress'].filter(
      id => !columns.some(col => col.id === id)
    );

    if (missingColumns.length === 0) return columns;

    const vatIndex = columns.findIndex(col => col.id === 'vat_rate');
    if (vatIndex === -1) return columns;

    const newColumns = [...columns];
    missingColumns.forEach((id, index) => {
      newColumns.splice(vatIndex + index, 0, { id });
    });

    return newColumns;
  };

  const defaultTableColumnLabels = {
    description: formatMessage({ id: 'pdf-preview.items' }, { language: displayLanguage }),
    quantity: formatMessage({ id: 'pdf-preview.quantity' }, { language: displayLanguage }),
    unit_price: formatMessage({ id: 'pdf-preview.unit-price' }, { language: displayLanguage }),
    progress: formatMessage({ id: 'pdf-preview.progress' }, { language: displayLanguage }),
    cumulative_progress: formatMessage(
      { id: 'pdf-preview.cumulative-progress' },
      { language: displayLanguage }
    ),
    vat_rate: formatMessage({ id: 'pdf-preview.vat' }, { language: displayLanguage }),
    subtotal: formatMessage({ id: 'pdf-preview.total-without-vat' }, { language: displayLanguage }),
  };

  const columns = getColumns().map(({ id, label }) => ({
    id,
    value: label || defaultTableColumnLabels[id as keyof typeof defaultTableColumnLabels],
  }));

  return { columns };
}
